import {
  IBooking,
  IPouchRows,
  bookingStatusType as BookingStatusTypes,
  BookingType,
  StatusUnconfirmed,
  BookingStateChangeTypes,
  IBookingFromPouch
} from 'shared-types/index';
import ObjectCopyService from "external-services/object-copy-service/index";
import AdminService from "external-services/diary-admin-service/admin.service";
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import axios, { AxiosInstance } from 'axios';
import {appURLS} from "appRoot/app.constants";
import BookingChangeService from "external-services/booking-change-service/bookingChange.service";
import NetworkRequestsService from 'external-services/network-requests-service/index';
import DateUtilsService from "external-services/date-utils-service";
import PouchLoadService from "external-services/pouch-load-service/index";
import store from 'appRoot/Store';
import PaymentService from 'app/services/payment/payment.service';
import { isBookingCreator } from 'app/reducers/settings/slice';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { editInBookingCreator } from 'app/reducers/booking/slice';
import ViewActionsService from 'app/actions/view.actions';

const NS = 'BookingService';

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export const AXIOS_INSTANCE = api;

class BookingService {

  public getBetweenDates(startDate: Date, endDate: Date): Promise<IBooking[]> {
    const startDateStr: string = DateUtilsService.dateToIdStr(startDate);
    const endDateStr: string = DateUtilsService.dateToIdStr(endDate);
    const database: any = PouchLoadService.selectDatabase(startDate, endDate);
    return Promise.resolve(database.allDocs({
      include_docs: true,
      attachments: true,
      startkey: 'booking_' + startDateStr,
      endkey: 'booking_' + endDateStr + '\ufff0'
    }).then((bookings: IPouchRows<IBookingFromPouch>) => {
      return bookings.rows.map((item: {doc: IBookingFromPouch}) => {
        const booking = BookingNormaliseService.normalizeModelToView(item.doc);
        return booking;
      });
    }));
  }

  public unAllocateBookingsTables(bookings: IBooking[]): Promise<void> {
    bookings = ObjectCopyService.angularSafeCopy(bookings);
    const bookingsToUpdate: {
      orgBooking: IBookingFromPouch,
      booking: IBookingFromPouch
    }[] = [];
    const bookingsToSave: IBookingFromPouch[] = [];
    const totalBookings: number = bookings.length;
    const venueTime: Date = DateUtilsService.getVenueTime(AdminService.getVenueTimezoneId());
    for (let i = totalBookings - 1; i >= 0; i--) {
      const booking: IBooking = bookings[i];
      delete booking.isSaving;
      delete booking.overdue;
      delete booking.bookingEnd;

      // check if the booking has tables
      if (booking.locked !== true && booking.tables && booking.tables.length &&
        booking.time.getTime() > venueTime.getTime() &&
        (booking.status.statusType === BookingStatusTypes.unconfirmed
          || booking.status.statusType === BookingStatusTypes.confirmed
          || booking.status.statusType === BookingStatusTypes.standbyPending
        )
      ) {
        const bookingWithStringDates: IBookingFromPouch = booking as unknown as IBookingFromPouch;
        bookingWithStringDates.time = DateUtilsService.dateToDateTimeStr(booking.time);
        const orgBooking: IBookingFromPouch = ObjectCopyService.angularSafeCopy(bookingWithStringDates);
        bookingWithStringDates.tables = [];
        if (bookingWithStringDates.bookingType === BookingType.StandbyList && bookingWithStringDates.status.statusType === BookingStatusTypes.standbyPending) {
          bookingWithStringDates.status = StatusUnconfirmed;
        } else {
          bookingWithStringDates.bookingType = BookingType.WaitList;
        }
        bookingWithStringDates.lastModifiedDate = new Date().toISOString();
        // push the changes
        bookingsToUpdate.push({
          orgBooking: orgBooking,
          booking: bookingWithStringDates
        });
        bookingsToSave.push(bookingWithStringDates);
      }
    }
    // save all booking which needed their table removed
    return PouchLoadService.getDbLocal().bulkDocs(bookingsToSave).then(() => {
      for (const bookingToUpdate of bookingsToUpdate) {
        // Track booking changes
        BookingChangeService.saveBookingChange(BookingStateChangeTypes.UPDATED, bookingToUpdate.booking, bookingToUpdate.orgBooking, null, null, `${NS}.unAllocateBookingsTables`);
      }
    });
  }

  public async downloadExcelReport(generatedDate: Date, fromDate: Date, toDate: Date): Promise<void> {
    if (AdminService.getVenue().isOnlineCustomersEnabled) {
      const data: {
        fromDate: string;
        toDate: string;
        currentDate: string;
      } = {
        fromDate: DateUtilsService.dateToDateTimeStr(fromDate),
        toDate: DateUtilsService.dateToDateTimeStr(toDate),
        currentDate: DateUtilsService.dateToDateTimeStr(generatedDate)
      }

      return await api.get(AdminService.getVenueExcelReportURL() + '?fromDate=' + data.fromDate + '&toDate=' + data.toDate + '&currentDate=' + data.currentDate, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
    } else {
      throw new Error(`'isOnlineCustomersEnabled' was set to null`);
    }
  }

  public finishAllBookings(date: Date): Promise<void> {
    const dateToFinishAll: string = encodeURIComponent(`${date.getFullYear()}/${date.getMonth()+1}/${date.getDate()}`);
    return new Promise<void>((resolve) => {
      const apiUrl = `${appURLS.API}/bookings/venues/${AdminService.getVenue().id}/finish-all?date=${dateToFinishAll}`;
      api.post(
        apiUrl,
        null,
      ).then(() => {
        resolve()
      }).catch((error: any) => {
        console.error('BookingService.finishAllBookings', date, error);
        resolve(null);
      });
    });
  }

  public reverseFinishAllBookings(date: Date): Promise<void> {
    const dateToReverseFinishAll: string = encodeURIComponent(`${date.getFullYear()}/${date.getMonth()+1}/${date.getDate()}`);
    return new Promise<void>((resolve) => {
      const apiUrl = `${appURLS.API}/bookings/venues/${AdminService.getVenue().id}/reverse-finish-all?date=${dateToReverseFinishAll}`;
      api.post(
        apiUrl,
        null,
      ).then(() => {
        resolve()
      }).catch((error: any) => {
        console.error('BookingService.reverseFinishAllBookings', date, error);
        resolve(null);
      });
    });
  }

  public handleModalBookingClicked = async (activeBooking: IBooking, dispatch: any) => {

    const bookingsByType = store.getState().filterBookingReducer.bookingsByType;

    //UI-1408: Check if booking is locked before editing
    const isLocked = PaymentService.conditionallyShowLockedBookingDialog(activeBooking);
    if (isLocked) {
      return;
    }
    if (isBookingCreator()) {
      /**
       * There is a property called 'tablesAvaiable' that gets added to WL bookings on bookingsByType, inside the
       * BookingListFilterService, but it does not get saved to the booking. So here we need look up the value and
       * attach it to the active booking again when opening it. It is responsible for displaying the "Seat Now"
       * button on the WaitList booking summary.
       */
      BookingAvailabilityService.addTablesAvailableFlagToCleanBooking(
        activeBooking,
        bookingsByType?.waitlist
      );

      dispatch(editInBookingCreator({activeBooking}));
    } else {
      ViewActionsService.onEditBooking({
        booking: activeBooking,
      });
    }
  }
}

const instance = new BookingService();
export default instance as BookingService;
